import "./global.css";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { HERO_PHOTOS, USECASE_PHOTOS, CTA_PHOTOS } from "./photos";
import { resolveRouteSeo } from "./config/seoConfig";
import { preloadImages } from "./utils/preload";
import AppRoutes from "./routes/routes";
import Seo from "./components/Seo";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import BackToTop from "./components/BackToTop";
import ThemeAuditOverlay from "./components/dev/ThemeAuditOverlay";

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Wait a frame so the routed page has laid out before we measure it —
      // scroll-margin-top on section/article elements accounts for the
      // fixed nav (see layout.css / utilities.css) so no manual offset here.
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
      });
      return () => window.cancelAnimationFrame(frame);
    }

    // Route changes should reset immediately so reveal observers see stable layout.
    window.scrollTo({ top: 0, behavior: "auto" });
    return undefined;
  }, [pathname, hash]);

  return null;
}

// Preload all photo pool images in the background so navigation feels instant
function PhotoPreloader() {
  useEffect(() => {
    preloadImages([...HERO_PHOTOS, ...USECASE_PHOTOS, ...CTA_PHOTOS]);
  }, []);
  return null;
}

function RouteSeo() {
  const { pathname } = useLocation();

  if (pathname.startsWith("/news") || pathname.startsWith("/research")) return null;

  const resolved = resolveRouteSeo(pathname);

  return (
    <Seo
      title={resolved.title}
      description={resolved.description}
      path={resolved.path}
      canonical={resolved.canonical}
      image={resolved.image}
      type={resolved.type}
      robots={resolved.robots}
      jsonLd={resolved.jsonLd}
      titleSuffix={resolved.titleSuffix}
    />
  );
}

export default function App() {
  return (
    <CookieConsentProvider>
      <ScrollToTop />
      <PhotoPreloader />
      <RouteSeo />
      <Nav />
      <main>
        <AppRoutes />
      </main>
      <Footer />
      <BackToTop />
      <CookieBanner />
      <ThemeAuditOverlay />
    </CookieConsentProvider>
  );
}
